// Super Master > Migração (pedido 2026-09-30): escolhe a empresa, depois o
// cliente, e vê as linhas dos rastreadores dele (linha / modelo / IMEI)
// com status ao vivo. Envia comando por SMS em massa por modelo do
// rastreador ou um comando diferente por linha. A lista revalida a cada
// 10 s e avisa (toast + histórico ao lado) cada rastreador que sai de
// offline pra online — é assim que se acompanha a reprogramação.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRightLeft,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Loader2,
  MessageSquare,
  RefreshCw,
  Send,
  Wifi,
  WifiOff,
  XCircle,
} from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { MasterSmsTabs } from "@/components/master-sms-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  useCompanies,
  useMigrationClients,
  useMigrationContext,
  useMigrationDevices,
} from "@/hooks/queries/platform";
import {
  gpsDrivePlatformMigrationService,
  type MigrationCommand,
  type MigrationDevice,
} from "@/services/gpsdrive-platform-migration.service";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/master/migration")({
  component: () => (
    <MasterShell>
      <MasterSmsTabs />
      <MigrationPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Migração — Super Master — GPS Drive" }] }),
});

const NO_MODEL = "Sem modelo cadastrado";
// Poucos SMS em paralelo — a Veye aceita, mas o saldo/erro de cada linha
// aparece em ordem e dá pra acompanhar.
const SEND_CONCURRENCY = 2;

type SendState =
  | { state: "sending" }
  | { state: "sent"; at: Date; text: string }
  | { state: "error"; message: string };

interface OnlineEvent {
  id: number;
  at: Date;
  name: string;
  line: string | null;
}

const isOnline = (d: MigrationDevice) => d.status === "online";

function formatLine(raw: string | null): string {
  if (!raw) return "—";
  const d = raw.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

function formatAgo(iso: string | null): string {
  if (!iso) return "nunca comunicou";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} dias`;
}

const timeFmt = (d: Date) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

function errorMessage(err: unknown): string {
  if (err instanceof PlatformApiError || err instanceof Error) return err.message;
  return "Falha ao enviar";
}

function MigrationPage() {
  const { data: companies } = useCompanies();
  const [companyId, setCompanyId] = useState<string>();
  const [clientId, setClientId] = useState<number>();
  const [clientOpen, setClientOpen] = useState(false);

  const { data: clients, isLoading: clientsLoading } = useMigrationClients(companyId);
  const { data: context, refetch: refetchContext } = useMigrationContext(companyId);
  const {
    data: devices,
    isLoading: devicesLoading,
    isFetching,
    dataUpdatedAt,
    refetch: refetchDevices,
  } = useMigrationDevices(companyId, clientId);

  const [balance, setBalance] = useState<number | null>(null);
  useEffect(() => setBalance(context?.sms.balance ?? null), [context]);

  const [sendState, setSendState] = useState<Record<number, SendState>>({});
  const [events, setEvents] = useState<OnlineEvent[]>([]);
  const [flash, setFlash] = useState<Set<number>>(new Set());

  // Troca de cliente/empresa zera tudo que é "desta sessão".
  const prevStatus = useRef<Map<number, MigrationDevice["status"]> | null>(null);
  useEffect(() => {
    prevStatus.current = null;
    setSendState({});
    setEvents([]);
    setFlash(new Set());
  }, [companyId, clientId]);

  // Detecta offline → online entre uma revalidação e outra.
  useEffect(() => {
    if (!devices) return;
    const prev = prevStatus.current;
    if (prev) {
      const cameOnline = devices.filter((d) => isOnline(d) && prev.has(d.id) && prev.get(d.id) !== "online");
      if (cameOnline.length > 0) {
        const now = new Date();
        setEvents((ev) => [
          ...cameOnline.map((d) => ({ id: d.id, at: now, name: d.name, line: d.line })),
          ...ev,
        ].slice(0, 200));
        for (const d of cameOnline) {
          toast.success(`${d.name} ficou ONLINE`, { description: `Linha ${formatLine(d.line)} · IMEI ${d.imei}` });
        }
        setFlash((f) => new Set([...f, ...cameOnline.map((d) => d.id)]));
        const ids = cameOnline.map((d) => d.id);
        window.setTimeout(() => setFlash((f) => new Set([...f].filter((id) => !ids.includes(id)))), 8000);
      }
    }
    prevStatus.current = new Map(devices.map((d) => [d.id, d.status]));
  }, [devices]);

  const groups = useMemo(() => {
    const map = new Map<string, MigrationDevice[]>();
    for (const d of devices ?? []) {
      const key = d.trackerModel?.trim() || NO_MODEL;
      map.set(key, [...(map.get(key) ?? []), d]);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a === NO_MODEL ? 1 : b === NO_MODEL ? -1 : a.localeCompare(b, "pt-BR")))
      .map(([model, list]) => ({
        model,
        devices: [...list].sort((a, b) => Number(isOnline(a)) - Number(isOnline(b)) || a.name.localeCompare(b.name)),
      }));
  }, [devices]);

  const total = devices?.length ?? 0;
  const onlineCount = devices?.filter(isOnline).length ?? 0;
  const client = clients?.find((c) => c.traccarUserId === clientId);
  const smsEnabled = context?.sms.enabled ?? false;

  const sendOne = async (device: MigrationDevice, text: string) => {
    if (!companyId) return;
    setSendState((s) => ({ ...s, [device.id]: { state: "sending" } }));
    try {
      const res = await gpsDrivePlatformMigrationService.send(companyId, device.id, text);
      setBalance(res.balanceAfter);
      setSendState((s) => ({ ...s, [device.id]: { state: "sent", at: new Date(), text } }));
    } catch (err) {
      setSendState((s) => ({ ...s, [device.id]: { state: "error", message: errorMessage(err) } }));
    }
  };

  const sendMany = async (targets: MigrationDevice[], text: string) => {
    const queue = targets.filter((d) => d.line);
    const skipped = targets.length - queue.length;
    await Promise.all(
      Array.from({ length: Math.min(SEND_CONCURRENCY, queue.length) }, async () => {
        for (let d = queue.shift(); d; d = queue.shift()) await sendOne(d, text);
      }),
    );
    if (skipped > 0) toast.warning(`${skipped} rastreador(es) sem linha cadastrada ficaram de fora`);
    void refetchContext();
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <ArrowRightLeft className="h-6 w-6 text-sky-500" /> Migração
        </h1>
        <p className="text-sm text-muted-foreground">
          Escolha a empresa e o cliente, envie os comandos por SMS para as linhas dos rastreadores e acompanhe
          aqui cada um que for ficando online. A lista atualiza sozinha a cada 10 segundos.
        </p>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Empresa</Label>
            <Select
              value={companyId}
              onValueChange={(v) => {
                setCompanyId(v);
                setClientId(undefined);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Escolha a empresa" />
              </SelectTrigger>
              <SelectContent>
                {(companies ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Popover open={clientOpen} onOpenChange={setClientOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  disabled={!companyId || clientsLoading}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {clientsLoading
                      ? "Carregando clientes…"
                      : client
                        ? client.name
                        : companyId
                          ? `Escolha o cliente (${clients?.length ?? 0})`
                          : "Escolha a empresa primeiro"}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar por nome ou e-mail…" />
                  <CommandList>
                    <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                    <CommandGroup>
                      {(clients ?? []).map((c) => (
                        <CommandItem
                          key={c.traccarUserId}
                          value={`${c.name} ${c.email} ${c.traccarUserId}`}
                          onSelect={() => {
                            setClientId(c.traccarUserId);
                            setClientOpen(false);
                          }}
                        >
                          <Check
                            className={cn("h-4 w-4", c.traccarUserId === clientId ? "opacity-100" : "opacity-0")}
                          />
                          <div className="min-w-0">
                            <div className="truncate">{c.name}</div>
                            <div className="truncate text-xs text-muted-foreground">{c.email}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label>SMS da empresa</Label>
            <div className="flex h-10 items-center gap-2 rounded-md border border-input px-3 text-sm">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              {!companyId ? (
                <span className="text-muted-foreground">—</span>
              ) : !context ? (
                <span className="text-muted-foreground">Carregando…</span>
              ) : smsEnabled ? (
                <span>
                  Saldo: <b>{(balance ?? 0).toLocaleString("pt-BR")}</b> SMS
                </span>
              ) : (
                <span className="text-rose-600">Integração de SMS desativada (ative em Super Master → SMS)</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {companyId && clientId && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_300px]">
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-white px-4 py-3 text-sm">
              <span className="font-semibold">{total} rastreador(es)</span>
              <span className="flex items-center gap-1.5 text-emerald-600">
                <Wifi className="h-4 w-4" /> {onlineCount} online
              </span>
              <span className="flex items-center gap-1.5 text-rose-600">
                <WifiOff className="h-4 w-4" /> {total - onlineCount} offline
              </span>
              <div className="h-2 min-w-[120px] flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: total ? `${(onlineCount / total) * 100}%` : 0 }}
                />
              </div>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Atualizado {dataUpdatedAt ? timeFmt(new Date(dataUpdatedAt)) : "—"}
              </span>
              <Button variant="ghost" size="sm" onClick={() => void refetchDevices()}>
                <RefreshCw className="h-3.5 w-3.5" /> Atualizar
              </Button>
            </div>

            {devicesLoading ? (
              <p className="text-sm text-muted-foreground">Carregando rastreadores…</p>
            ) : total === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  Este cliente não tem nenhum rastreador vinculado.
                </CardContent>
              </Card>
            ) : (
              groups.map((g) => (
                <ModelGroup
                  key={g.model}
                  model={g.model}
                  devices={g.devices}
                  commands={context?.commands ?? []}
                  smsEnabled={smsEnabled}
                  sendState={sendState}
                  flash={flash}
                  onSendOne={sendOne}
                  onSendMany={sendMany}
                />
              ))
            )}
          </div>

          <Card className="h-fit xl:sticky xl:top-4">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Wifi className="h-4 w-4 text-emerald-600" /> Ficaram online
              </div>
              {events.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Assim que um rastreador offline começar a comunicar, ele aparece aqui.
                </p>
              ) : (
                <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
                  {events.map((e, i) => (
                    <li key={`${e.id}-${i}`} className="rounded-md bg-emerald-50 px-3 py-2 text-xs">
                      <div className="font-medium text-emerald-800">{e.name}</div>
                      <div className="text-emerald-700">
                        {formatLine(e.line)} · {timeFmt(e.at)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function ModelGroup({
  model,
  devices,
  commands,
  smsEnabled,
  sendState,
  flash,
  onSendOne,
  onSendMany,
}: {
  model: string;
  devices: MigrationDevice[];
  commands: MigrationCommand[];
  smsEnabled: boolean;
  sendState: Record<number, SendState>;
  flash: Set<number>;
  onSendOne: (d: MigrationDevice, text: string) => Promise<void>;
  onSendMany: (targets: MigrationDevice[], text: string) => Promise<void>;
}) {
  const [bulkText, setBulkText] = useState("");
  const [onlyOffline, setOnlyOffline] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [rowText, setRowText] = useState<Record<number, string>>({});

  // Comandos do catálogo do mesmo protocolo desse modelo primeiro.
  const protocols = new Set(devices.map((d) => d.protocol).filter(Boolean));
  const sortedCommands = [...commands].sort(
    (a, b) => Number(protocols.has(b.protocol)) - Number(protocols.has(a.protocol)),
  );

  const onlineCount = devices.filter(isOnline).length;
  const targets = devices.filter((d) => d.line && (!onlyOffline || !isOnline(d)));

  useEffect(() => {
    setConfirming(false);
  }, [bulkText, onlyOffline]);

  const runBulk = async () => {
    setConfirming(false);
    setSending(true);
    await onSendMany(targets, bulkText.trim());
    setSending(false);
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="text-sm">
            <span className="font-semibold">{model}</span>{" "}
            <span className="text-muted-foreground">
              · {devices.length} linha(s) · <span className="text-emerald-600">{onlineCount} online</span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 px-4">
          <div className="min-w-[180px] space-y-1">
            <Label className="text-xs">Comando do catálogo</Label>
            <Select value="" onValueChange={(id) => setBulkText(commands.find((c) => c.id === id)?.text ?? "")}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Escolher…" />
              </SelectTrigger>
              <SelectContent>
                {sortedCommands.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Empresa sem comandos cadastrados</div>
                )}
                {sortedCommands.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} <span className="text-muted-foreground">({c.protocol})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[240px] flex-1 space-y-1">
            <Label className="text-xs">Comando para todas as linhas deste modelo</Label>
            <Input
              className="h-9 font-mono text-xs"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Ex.: SERVER,0,rastreio.exemplo.com,5023,0#"
            />
          </div>
          <label className="flex h-9 items-center gap-2 text-xs">
            <Checkbox checked={onlyOffline} onCheckedChange={(v) => setOnlyOffline(v === true)} />
            Só os offline
          </label>
          {confirming ? (
            <div className="flex gap-1">
              <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700" onClick={() => void runBulk()}>
                Confirmar {targets.length} SMS
              </Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              className="h-9 gap-1.5"
              disabled={!smsEnabled || !bulkText.trim() || targets.length === 0 || sending}
              onClick={() => setConfirming(true)}
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Enviar para {targets.length}
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Linha</th>
                <th className="px-3 py-2 font-medium">Modelo</th>
                <th className="px-3 py-2 font-medium">IMEI</th>
                <th className="px-3 py-2 font-medium">Veículo</th>
                <th className="px-3 py-2 font-medium">Comando manual</th>
                <th className="px-4 py-2 font-medium">Envio</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const st = sendState[d.id];
                const text = rowText[d.id] ?? "";
                const online = isOnline(d);
                return (
                  <tr
                    key={d.id}
                    className={cn(
                      "border-t transition-colors duration-700",
                      flash.has(d.id) && "bg-emerald-100",
                    )}
                  >
                    <td className="whitespace-nowrap px-4 py-2">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
                          online ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
                        )}
                      >
                        <span className={cn("h-2 w-2 rounded-full", online ? "bg-emerald-500" : "bg-rose-500")} />
                        {online ? "Online" : "Offline"}
                      </span>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{formatAgo(d.lastUpdate)}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                      {formatLine(d.line)}
                      {d.carrier && <div className="font-sans text-[11px] text-muted-foreground">{d.carrier}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">{d.trackerModel || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{d.imei}</td>
                    <td className="max-w-[160px] truncate px-3 py-2 text-xs">{d.name}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Input
                          className="h-8 min-w-[180px] font-mono text-xs"
                          value={text}
                          onChange={(e) => setRowText((r) => ({ ...r, [d.id]: e.target.value }))}
                          placeholder={d.line ? "Comando só pra esta linha" : "Sem linha cadastrada"}
                          disabled={!d.line}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && text.trim() && smsEnabled && st?.state !== "sending")
                              void onSendOne(d, text.trim());
                          }}
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 shrink-0"
                          title="Enviar SMS"
                          disabled={!d.line || !text.trim() || !smsEnabled || st?.state === "sending"}
                          onClick={() => void onSendOne(d, text.trim())}
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs">
                      {!st ? (
                        <span className="text-muted-foreground">—</span>
                      ) : st.state === "sending" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      ) : st.state === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700" title={st.text}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Enviado {timeFmt(st.at)}
                        </span>
                      ) : (
                        <span className="inline-flex max-w-[220px] items-center gap-1 text-rose-600" title={st.message}>
                          <XCircle className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{st.message}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
